import React, { useState, useRef, useEffect } from "react";
import { Plane, MapPin, Anchor, Building2, Home, Loader2, Landmark, Waves, PenLine } from "lucide-react";
import { LOCATIONS, registerPlace, AREA_NAMES } from "../../mock";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { api } from "../../lib/api";

const iconFor = (l) => {
  if (l.type === "airport") return Plane;
  if (l.type === "port") return Anchor;
  if (l.type === "hotel") return Building2;
  if (l.type === "villa") return Home;
  if (l.tag === "Beach") return Waves;
  if (l.tag === "Landmark") return Landmark;
  return MapPin;
};

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const Option = ({ l, onPick }) => {
  const I = iconFor(l);
  return (
    <li>
      <button type="button" data-testid="location-option" onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(l)}
        className="w-full flex items-start gap-3 px-3.5 py-2.5 text-left hover:bg-[#F6F2E9] transition-colors">
        <I className="w-4 h-4 mt-0.5 text-[#3E7F8E] shrink-0" />
        <span className="text-[14.5px] text-[#202522] leading-snug">
          {l.name}
          {l.tag && <span className="ml-2 inline-block align-middle text-[10.5px] px-2 py-0.5 rounded-full bg-[#E4EFF0] text-[#3E7F8E] font-medium">{l.tag}</span>}
        </span>
      </button>
    </li>
  );
};

const GroupLabel = ({ children }) => (
  <li className="px-3.5 pt-2.5 pb-1 text-[10.5px] tracking-[0.14em] uppercase font-semibold text-[#202522]/45">{children}</li>
);

const LocationInput = ({ label, value, onChange, placeholder, icon: LeadIcon = MapPin, testid }) => {
  const [q, setQ] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [remote, setRemote] = useState([]);
  const [loading, setLoading] = useState(false);
  const [custom, setCustom] = useState("");
  const ref = useRef(null);

  useEffect(() => { setQ(value || ""); }, [value]);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const words = norm(q).split(/\s+/).filter(Boolean);
  const local = q.trim().length < 2 ? [] : LOCATIONS.filter((l) => {
    const n = norm(`${l.name} ${l.tag || ""} ${l.alias || ""}`);
    return words.every((w) => n.includes(w));
  }).slice(0, 6);

  // live search for villas, hotels & addresses (debounced; re-polls while slower provider finishes)
  useEffect(() => {
    if (!open || q.trim().length < 3 || q === value) { setRemote([]); setLoading(false); return; }
    let cancelled = false;
    let timer;
    setLoading(true);
    const localNames = new Set(LOCATIONS.map((l) => norm(l.name)));
    const run = async (attempt) => {
      try {
        const { data } = await api.get("/places/search", { params: { q: q.trim() } });
        if (cancelled) return;
        setRemote((data.results || []).filter((r) => !localNames.has(norm(r.name))).slice(0, 8));
        if (data.partial && attempt < 6) { timer = setTimeout(() => run(attempt + 1), 2500); return; }
      } catch { if (!cancelled) setRemote([]); }
      if (!cancelled) setLoading(false);
    };
    timer = setTimeout(() => run(0), 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [q, open, value]);

  const pick = (l) => {
    setCustom("");
    if (l.lat !== undefined) registerPlace(l);
    onChange(l.name);
    setQ(l.name);
    setOpen(false);
  };

  const canCustom = q.trim().length >= 4 && q !== value;
  const showList = open && q.trim().length >= 2 && (local.length > 0 || remote.length > 0 || loading || canCustom);

  return (
    <div className="relative" ref={ref}>
      <label className="block text-[10.5px] sm:text-[12px] tracking-[0.08em] uppercase font-medium text-[#3E7F8E] mb-1 sm:mb-2">{label}</label>
      <div className="flex items-center gap-2.5 h-[42px] sm:h-[46px] rounded-xl border border-[#183C35]/15 bg-white px-3.5 focus-within:border-[#183C35] focus-within:ring-2 focus-within:ring-[#183C35]/10 transition-[border-color,box-shadow] duration-200">
        <LeadIcon className="w-4 h-4 text-[#3E7F8E] shrink-0" />
        <input
          value={q}
          data-testid={testid}
          onChange={(e) => { setQ(e.target.value); onChange(""); setCustom(""); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="w-full bg-transparent outline-none text-[16px] sm:text-[15px] text-[#202522] placeholder:text-[#202522]/40"
        />
        {loading && open && <Loader2 className="w-4 h-4 text-[#3E7F8E] animate-spin shrink-0" />}
      </div>
      {showList && (
        <ul className="absolute z-30 left-0 right-0 mt-1.5 bg-white rounded-xl border border-[#183C35]/10 shadow-xl py-1.5 max-h-80 overflow-auto fade-up" style={{ animationDuration: ".2s" }} data-testid={`${testid}-results`}>
          {local.length > 0 && remote.length > 0 && <GroupLabel>Popular places</GroupLabel>}
          {local.map((l) => <Option key={`l-${l.name}`} l={l} onPick={pick} />)}
          {remote.length > 0 && <GroupLabel>Hotels, villas &amp; addresses</GroupLabel>}
          {remote.map((l) => <Option key={`r-${l.name}-${l.lat}`} l={l} onPick={pick} />)}
          {loading && remote.length === 0 && local.length === 0 && (
            <li className="px-3.5 py-3 text-[14px] text-[#202522]/55 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Searching Corfu...</li>
          )}
          {loading && (remote.length > 0 || local.length > 0) && (
            <li className="px-3.5 py-2 text-[12.5px] text-[#202522]/50 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" />Looking for more villas &amp; hotels...</li>
          )}
          {canCustom && (
            <li className="border-t border-[#183C35]/10 mt-1 pt-1">
              <button type="button" data-testid={`${testid}-custom`} onMouseDown={(e) => e.preventDefault()}
                onClick={() => { setCustom(q.trim()); setOpen(false); onChange(""); }}
                className="w-full flex items-start gap-3 px-3.5 py-2.5 text-left hover:bg-[#F6F2E9] transition-colors">
                <PenLine className="w-4 h-4 mt-0.5 text-[#3E7F8E] shrink-0" />
                <span className="text-[14px] text-[#202522] leading-snug">Use "<strong>{q.trim()}</strong>" as my address<span className="block text-[12px] text-[#202522]/55">Private villa or address not listed? Choose the nearest area next.</span></span>
              </button>
            </li>
          )}
        </ul>
      )}
      {custom && !value && (
        <div className="mt-2 fade-up" style={{ animationDuration: ".2s" }}>
          <Select onValueChange={(k) => { registerPlace({ name: custom, key: k }); onChange(custom); setQ(custom); setCustom(""); }}>
            <SelectTrigger className="h-[42px] rounded-xl border-[#3E7F8E]/40 bg-[#F4F9F9] text-[14px]" data-testid={`${testid}-area-select`}>
              <SelectValue placeholder="Which area is it in? (for a fixed price)" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {Object.entries(AREA_NAMES).map(([k, n]) => <SelectItem key={k} value={k} data-testid={`area-option-${k}`}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
};

export default LocationInput;
